(() => {
  // Cart nutrition totals, weekly goal progress and "Recommended meals".
  // Adding a recommended meal to the cart is handled in assets/cm-order-rules.js.
  //
  // This file is included by both layout/theme.liquid and snippets/cart-drawer.liquid,
  // so it must only ever start once.
  if (window.__cmCartNutrition) return;
  window.__cmCartNutrition = true;
  const STORAGE_KEY = 'countingMacrosGoals';
  const MAX_RECOMMENDATIONS = 3;

  let nutritionUpdateTimer = null;
  let observerUpdateTimer = null;
  let recommendationSignature = '';
  let mealPreferences = null;

  const MACRO_KEYS = ['calories', 'protein', 'carbs', 'fat'];
  const MACRO_LABELS = {
    calories: 'calories',
    protein: 'protein',
    carbs: 'carbohydrates',
    fat: 'fat'
  };
  const MACRO_UNITS = { calories: 'kcal', protein: 'g', carbs: 'g', fat: 'g' };
  const PROTEIN_KEYWORDS = {
    chicken: ['chicken'],
    turkey: ['turkey'],
    beef: ['beef', 'steak', 'bison', 'burger', 'meatloaf'],
    pork: ['pork', 'ham', 'bacon', 'sausage'],
    lamb: ['lamb'],
    fish: ['fish', 'salmon', 'tuna', 'cod', 'tilapia', 'mahi'],
    seafood: ['seafood', 'shrimp', 'prawn', 'crab', 'lobster', 'shellfish'],
    vegetarian: ['vegetarian', 'veggie', 'tofu', 'plant based'],
    plant_based: ['plant based', 'vegan', 'tofu', 'tempeh']
  };

  // Customer allergy choices mapped to Shopify's "Allergen information" entries
  // (product metafield shopify.allergen-information). "nuts" is treated as a
  // match for both peanut and tree-nut allergies.
  const ALLERGEN_HANDLES = {
    peanuts: ['peanuts', 'peanut', 'nuts'],
    tree_nuts: ['nuts', 'tree-nuts', 'tree-nut'],
    milk_dairy: ['milk', 'dairy', 'lactose'],
    eggs: ['eggs', 'egg'],
    wheat_gluten: ['wheat', 'gluten', 'cereals-containing-gluten'],
    soy: ['soybeans', 'soy', 'soya'],
    sesame: ['sesame', 'sesame-seeds'],
    fish: ['fish'],
    shellfish: ['crustaceans', 'molluscs', 'mollusks', 'shellfish']
  };

  // A second, word-based safety net over the ingredient list and description.
  // It can only remove a meal from recommendations, never approve one.
  const ALLERGY_KEYWORDS = {
    peanuts: ['peanut'],
    tree_nuts: ['almond', 'cashew', 'walnut', 'pecan', 'pistachio', 'hazelnut', 'macadamia', 'pine nut', 'pine nuts', 'tree nut'],
    milk_dairy: ['milk', 'dairy', 'cheese', 'cream', 'butter', 'yogurt', 'whey', 'casein', 'mozzarella', 'parmesan', 'pecorino', 'ricotta', 'feta'],
    eggs: ['egg', 'eggs', 'mayonnaise', 'mayo'],
    wheat_gluten: ['wheat', 'gluten', 'bread', 'pasta', 'penne', 'spaghetti', 'flour', 'waffle', 'crepe', 'orzo', 'soy sauce'],
    soy: ['soy', 'tofu', 'tempeh', 'edamame'],
    sesame: ['sesame', 'tahini'],
    fish: ['fish', 'salmon', 'tuna', 'cod', 'tilapia', 'mahi', 'anchovy', 'worcestershire'],
    shellfish: ['shellfish', 'shrimp', 'prawn', 'crab', 'lobster']
  };

  // Only touch the page when a value actually changes. Rewriting identical text
  // counts as a page change, which used to re-trigger this script's own
  // watcher in an endless loop on the cart page.
  function setText(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function number(value) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getSavedGoals() {
    try {
      const storedGoals = localStorage.getItem(STORAGE_KEY);
      if (!storedGoals) return null;

      const goals = JSON.parse(storedGoals);
      if (!goals || typeof goals !== 'object') return null;

      const dailyGoals = {
        calories: number(goals.calories),
        protein: number(goals.protein),
        carbs: number(goals.carbs),
        fat: number(goals.fat)
      };

      if (dailyGoals.calories <= 0 || dailyGoals.protein <= 0) return null;
      return dailyGoals;
    } catch (error) {
      console.warn('Counting Macros goals could not be loaded.', error);
      return null;
    }
  }

  function getWeeklyGoals() {
    const dailyGoals = getSavedGoals();
    if (!dailyGoals) return null;

    return {
      calories: dailyGoals.calories * 7,
      protein: dailyGoals.protein * 7,
      carbs: dailyGoals.carbs * 7,
      fat: dailyGoals.fat * 7
    };
  }

  function getCartTotals(root) {
    const items = root.querySelectorAll('[data-cm-nutrition-item]');
    const totals = { calories: 0, protein: 0, carbs: 0, fat: 0, meals: 0 };

    items.forEach((item) => {
      const quantityInput = item.querySelector(
        'input[name="updates[]"], input[type="number"], [data-quantity-input]'
      );

      const quantity = quantityInput
        ? Math.max(0, number(quantityInput.value))
        : 1;

      totals.calories += number(item.dataset.cmCalories) * quantity;
      totals.protein += number(item.dataset.cmProtein) * quantity;
      totals.carbs += number(item.dataset.cmCarbs) * quantity;
      totals.fat += number(item.dataset.cmFat) * quantity;
      totals.meals += quantity;
    });

    return totals;
  }

  function updateCartSummaries(root, totals) {
    root.querySelectorAll('.cm-cart-nutrition, .cm-drawer-nutrition').forEach((summary) => {
      const caloriesTarget = summary.querySelector('[data-cm-cart-calories], [data-cm-drawer-calories]');
      const proteinTarget = summary.querySelector('[data-cm-cart-protein], [data-cm-drawer-protein]');
      const carbsTarget = summary.querySelector('[data-cm-cart-carbs], [data-cm-drawer-carbs]');
      const fatTarget = summary.querySelector('[data-cm-cart-fat], [data-cm-drawer-fat]');
      const mealsTarget = summary.querySelector('[data-cm-cart-meals], [data-cm-drawer-meals]');

      setText(caloriesTarget, `${Math.round(totals.calories)} kcal`);
      setText(proteinTarget, `${Math.round(totals.protein)} g`);
      setText(carbsTarget, `${Math.round(totals.carbs)} g`);
      setText(fatTarget, `${Math.round(totals.fat)} g`);

      setText(mealsTarget, `${totals.meals} ${totals.meals === 1 ? 'meal' : 'meals'} selected`);
    });
  }

  function getGoalStatus(current, target) {
    const safeTarget = number(target);
    if (safeTarget <= 0) return { percentage: 0, status: 'low' };

    const rawPercentage = (current / safeTarget) * 100;
    let status = 'low';

    if (rawPercentage > 110) status = 'over';
    else if (rawPercentage >= 90) status = 'reached';
    else if (rawPercentage >= 50) status = 'close';

    return {
      percentage: Math.min(rawPercentage, 100),
      status
    };
  }

  const goalCelebrationStatuses = new Map();
  const GOAL_CELEBRATIONS = {
    calories: { icon: '🔥', label: 'Calories' },
    protein: { icon: '🥩', label: 'Protein' },
    carbs: { icon: '🍚', label: 'Carbohydrates' },
    fat: { icon: '🥑', label: 'Fat' }
  };

  function ensureGoalCelebrationContainer() {
    let container = document.querySelector('[data-cm-goal-celebrations]');
    if (container) return container;

    if (!document.getElementById('cm-goal-celebration-styles')) {
      const style = document.createElement('style');
      style.id = 'cm-goal-celebration-styles';
      style.textContent = [
        '.cm-goal-celebrations{position:fixed;top:clamp(78px,12vh,140px);left:50%;transform:translateX(-50%);z-index:2147483000;display:grid;gap:12px;width:min(420px,calc(100vw - 28px));pointer-events:none}',
        '.cm-goal-celebration{position:relative;display:flex;align-items:center;gap:14px;overflow:visible;padding:17px 20px;border:2px solid #77b784;border-radius:22px;background:linear-gradient(135deg,#fff 0%,#eef9f0 52%,#dcf3e1 100%);box-shadow:0 18px 50px rgba(33,95,54,.24);color:#173d25;animation:cm-goal-pop 2.8s ease both}',
        '.cm-goal-celebration__icon{display:grid;place-items:center;flex:0 0 54px;width:54px;height:54px;border-radius:50%;background:#fff;font-size:30px;box-shadow:0 8px 20px rgba(33,95,54,.16)}',
        '.cm-goal-celebration__copy{position:relative;z-index:2}',
        '.cm-goal-celebration__title{font-size:19px;font-weight:800;line-height:1.15}',
        '.cm-goal-celebration__message{margin-top:4px;font-size:14px;font-weight:600;color:#497454}',
        '.cm-goal-celebration__confetti{position:absolute;inset:0;overflow:visible;pointer-events:none}',
        '.cm-goal-celebration__confetti span{position:absolute;left:50%;top:48%;font-size:18px;opacity:0;animation:cm-goal-confetti 1.45s ease-out var(--cm-delay) forwards}',
        '@keyframes cm-goal-pop{0%{opacity:0;transform:translateY(24px) scale(.7)}14%{opacity:1;transform:translateY(0) scale(1.07)}24%,80%{opacity:1;transform:translateY(0) scale(1)}100%{opacity:0;transform:translateY(-20px) scale(.96)}}',
        '@keyframes cm-goal-confetti{0%{opacity:0;transform:translate(-50%,-50%) scale(.4) rotate(0deg)}16%{opacity:1}100%{opacity:0;transform:translate(var(--cm-x),var(--cm-y)) scale(1.08) rotate(var(--cm-r))}}',
        '@media (prefers-reduced-motion:reduce){.cm-goal-celebration{animation:cm-goal-fade 2.8s ease both}.cm-goal-celebration__confetti{display:none}@keyframes cm-goal-fade{0%,100%{opacity:0}12%,82%{opacity:1}}}',
        '@media (max-width:520px){.cm-goal-celebration{padding:14px 16px;border-radius:18px}.cm-goal-celebration__icon{flex-basis:46px;width:46px;height:46px;font-size:25px}.cm-goal-celebration__title{font-size:17px}}'
      ].join('');
      document.head.appendChild(style);
    }

    container = document.createElement('div');
    container.className = 'cm-goal-celebrations';
    container.dataset.cmGoalCelebrations = '';
    container.setAttribute('aria-live', 'polite');
    document.body.appendChild(container);
    return container;
  }

  function showGoalCelebration(name) {
    const details = GOAL_CELEBRATIONS[name];
    if (!details) return;

    const popup = document.createElement('div');
    popup.className = 'cm-goal-celebration';
    popup.setAttribute('role', 'status');

    const icon = document.createElement('div');
    icon.className = 'cm-goal-celebration__icon';
    icon.textContent = details.icon;

    const copy = document.createElement('div');
    copy.className = 'cm-goal-celebration__copy';
    const title = document.createElement('div');
    title.className = 'cm-goal-celebration__title';
    title.textContent = details.label + ' goal zone reached!';
    const message = document.createElement('div');
    message.className = 'cm-goal-celebration__message';
    message.textContent = 'Great balance — you’re right on target. 🎉';
    copy.append(title, message);

    const confetti = document.createElement('div');
    confetti.className = 'cm-goal-celebration__confetti';
    ['🎉', '✨', '⭐', '💚', '🎊', '✨', '🎉', '⭐'].forEach((symbol, index) => {
      const particle = document.createElement('span');
      particle.textContent = symbol;
      particle.style.setProperty('--cm-x', String(-155 + index * 44) + 'px');
      particle.style.setProperty('--cm-y', String(-78 - (index % 3) * 25) + 'px');
      particle.style.setProperty('--cm-r', String(-90 + index * 43) + 'deg');
      particle.style.setProperty('--cm-delay', String(index * 0.045) + 's');
      confetti.appendChild(particle);
    });

    popup.append(icon, copy, confetti);
    ensureGoalCelebrationContainer().appendChild(popup);
    window.setTimeout(() => popup.remove(), 2800);
  }

  function updateGoalCelebration(name, status) {
    const previousStatus = goalCelebrationStatuses.get(name);
    goalCelebrationStatuses.set(name, status);
    if (previousStatus !== undefined && previousStatus !== 'reached' && status === 'reached') {
      showGoalCelebration(name);
    }
  }

  // Plain-language "how much more" line shown under each goal bar.
  function remainingLine(current, target, unit, status) {
    const safeTarget = Number(target) || 0;
    if (safeTarget <= 0) return {text: '', status: 'low'};
    const difference = Math.round(safeTarget - current);
    if (status === 'over') {
      return {text: `${Math.abs(difference).toLocaleString()} ${unit} over your weekly goal`, status: 'over'};
    }
    if (difference > 0) {
      return {text: `Still need ${difference.toLocaleString()} ${unit} this week`, status: 'remaining'};
    }
    return {text: 'Weekly goal met ✓', status: 'reached'};
  }

  function updateGoal(dashboard, name, current, target, unit) {
    const text = dashboard.querySelector(`[data-cm-goal-${name}-text]`);
    const bar = dashboard.querySelector(`[data-cm-goal-${name}-bar]`);
    const progress = dashboard.querySelector(`[data-cm-goal-${name}-progress]`);

    const safeTarget = number(target);
    const goalStatus = getGoalStatus(current, safeTarget);
    updateGoalCelebration(name, goalStatus.status);

    setText(text, `${Math.round(current)} / ${Math.round(safeTarget)} ${unit}`);

    const remaining = dashboard.querySelector(`[data-cm-goal-${name}-remaining]`);
    if (remaining) {
      const line = remainingLine(current, safeTarget, unit, goalStatus.status);
      setText(remaining, line.text);
      if (remaining.dataset.status !== line.status) remaining.dataset.status = line.status;
    }

    if (bar) {
      bar.style.width = `${goalStatus.percentage}%`;
      bar.dataset.status = goalStatus.status;
    }

    if (progress) {
      progress.setAttribute('aria-valuenow', String(Math.round(goalStatus.percentage)));
    }
  }

  function createGoalMessage(totals, weeklyGoals) {
    const caloriesRemaining = Math.round(weeklyGoals.calories - totals.calories);
    const proteinRemaining = Math.round(weeklyGoals.protein - totals.protein);

    if (caloriesRemaining <= 0 && proteinRemaining <= 0) {
      return {
        status: 'reached',
        text: 'Your selected meals have reached your weekly calorie and protein targets.'
      };
    }

    if (caloriesRemaining > 0 && proteinRemaining > 0) {
      return {
        status: 'remaining',
        text: `${caloriesRemaining.toLocaleString()} calories and ${proteinRemaining.toLocaleString()} g of protein remaining this week.`
      };
    }

    if (caloriesRemaining > 0) {
      return {
        status: 'remaining',
        text: `${caloriesRemaining.toLocaleString()} calories remaining this week. Your protein target is already covered.`
      };
    }

    return {
      status: 'remaining',
      text: `${proteinRemaining.toLocaleString()} g of protein remaining this week. Prioritize lean, high-protein meals.`
    };
  }

  function updateGoalsDashboard(root, totals, weeklyGoals) {
    root.querySelectorAll('[data-cm-goals-dashboard]').forEach((dashboard) => {
      if (!weeklyGoals) {
        dashboard.hidden = true;
        return;
      }

      dashboard.hidden = false;

      updateGoal(dashboard, 'calories', totals.calories, weeklyGoals.calories, 'kcal');
      updateGoal(dashboard, 'protein', totals.protein, weeklyGoals.protein, 'g');
      updateGoal(dashboard, 'carbs', totals.carbs, weeklyGoals.carbs, 'g');
      updateGoal(dashboard, 'fat', totals.fat, weeklyGoals.fat, 'g');

      const messageElement = dashboard.querySelector('[data-cm-goal-message]');
      if (messageElement) {
        const message = createGoalMessage(totals, weeklyGoals);
        setText(messageElement, message.text);
        messageElement.dataset.status = message.status;
      }
    });
  }

  function readRecommendationProducts(root) {
    return Array.from(root.querySelectorAll('[data-cm-recommendation-product]'))
      .map((element) => ({
        productId: element.dataset.cmProductId || '',
        variantId: element.dataset.cmVariantId || '',
        title: element.dataset.cmTitle || '',
        url: element.dataset.cmUrl || '#',
        image: element.dataset.cmImage || '',
        price: element.dataset.cmPrice || '',
        calories: number(element.dataset.cmCalories),
        protein: number(element.dataset.cmProtein),
        carbs: number(element.dataset.cmCarbs),
        fat: number(element.dataset.cmFat),
        tags: element.dataset.cmTags || '',
        productType: element.dataset.cmProductType || '',
        description: element.dataset.cmDescription || '',
        ingredients: element.dataset.cmIngredients || '',
        allergens: String(element.dataset.cmAllergens || '')
          .split('|')
          .map((handle) => handle.trim().toLowerCase())
          .filter(Boolean),
        allergensReviewed: element.dataset.cmAllergensReviewed === 'true'
      }))
      .filter((product) => product.variantId && product.title && product.calories > 0);
  }

  function getCartProductIds(root) {
    return new Set(
      Array.from(root.querySelectorAll('[data-cm-nutrition-item]'))
        .map((item) => item.dataset.productId || item.dataset.cmProductId)
        .filter(Boolean)
        .map(String)
    );
  }

  function normalizeProductText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function getProductSearchText(product) {
    return normalizeProductText([
      product.title,
      product.tags,
      product.productType,
      product.description,
      product.ingredients
    ].join(' '));
  }

  function matchesKeywords(searchText, keywords) {
    const paddedText = ` ${searchText} `;
    return (keywords || []).some((keyword) => {
      const normalizedKeyword = normalizeProductText(keyword);
      return normalizedKeyword && paddedText.includes(` ${normalizedKeyword} `);
    });
  }

  function getPreferenceMatch(product, preferences) {
    if (!preferences) return { allowed: true, preferred: false };

    const searchText = getProductSearchText(product);
    const excludedProteins = Array.isArray(preferences.excludedProteins)
      ? preferences.excludedProteins
      : [];
    if (excludedProteins.some((protein) => matchesKeywords(searchText, PROTEIN_KEYWORDS[protein]))) {
      return { allowed: false, preferred: false };
    }

    const allergies = Array.isArray(preferences.allergies) ? preferences.allergies : [];
    const otherAllergyKeywords = String(preferences.otherAllergies || '')
      .split(/[,;\n/]+/)
      .map((item) => normalizeProductText(item))
      .filter((item) => item.length >= 3);

    if (allergies.length > 0 || otherAllergyKeywords.length > 0) {
      // Someone with allergies is only ever shown meals whose allergen
      // information has been checked by the kitchen.
      if (!product.allergensReviewed) return { allowed: false, preferred: false };

      const blockedByAllergen = allergies.some((allergy) => {
        const handles = ALLERGEN_HANDLES[allergy];
        if (!handles) return true; // An allergy this page does not know how to check.
        return handles.some((handle) => product.allergens.includes(handle));
      });
      const blockedByKeyword = allergies.some((allergy) =>
        matchesKeywords(searchText, ALLERGY_KEYWORDS[allergy])
      );
      const blockedByOtherAllergy = otherAllergyKeywords.length > 0 && (
        !product.ingredients || matchesKeywords(searchText, otherAllergyKeywords)
      );

      if (blockedByAllergen || blockedByKeyword || blockedByOtherAllergy) {
        return { allowed: false, preferred: false };
      }
    }

    const preferredProteins = Array.isArray(preferences.proteins)
      ? preferences.proteins
      : [];
    const preferred = preferredProteins.some((protein) =>
      matchesKeywords(searchText, PROTEIN_KEYWORDS[protein])
    );
    return { allowed: true, preferred };
  }

  function getPrimaryMacro(gaps, weeklyGoals) {
    return MACRO_KEYS
      .map((key) => ({
        key,
        remaining: Math.max(number(gaps[key]), 0),
        ratio: number(weeklyGoals[key]) > 0
          ? Math.max(number(gaps[key]), 0) / number(weeklyGoals[key])
          : 0
      }))
      .sort((left, right) => right.ratio - left.ratio)[0];
  }

  function scoreProduct(product, gaps, weeklyGoals) {
    return MACRO_KEYS.reduce((score, key) => {
      const remaining = Math.max(number(gaps[key]), 0);
      const target = Math.max(number(weeklyGoals[key]), 1);
      const coverage = remaining > 0
        ? Math.min(number(product[key]) / remaining, 1.5)
        : 0;
      return score + coverage * (remaining / target);
    }, 0);
  }

  function getRecommendationReason(product) {
    const primary = product.primaryMacro;
    if (!primary) return 'A balanced option based on your remaining macros.';

    let reason = `High in ${MACRO_LABELS[primary.key]} to help close your largest remaining gap.`;
    if (product.preferenceMatch) {
      reason += ' Matches your saved meal preferences.';
    }
    return reason;
  }

  function createRecommendationCard(product) {
    const imageMarkup = product.image
      ? `<img class="cm-recommendation-card__image" src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="500" height="375">`
      : '';

    return `
      <article class="cm-recommendation-card">
        <a class="cm-recommendation-card__image-link" href="${escapeHtml(product.url)}">
          ${imageMarkup}
        </a>
        <div class="cm-recommendation-card__body">
          <a class="cm-recommendation-card__title" href="${escapeHtml(product.url)}">
            ${escapeHtml(product.title)}
          </a>
          <p class="cm-recommendation-card__macros">
            ${Math.round(product.calories)} kcal ·
            ${Math.round(product.protein)} g protein ·
            ${Math.round(product.carbs)} g carbs ·
            ${Math.round(product.fat)} g fat
          </p>
          <p class="cm-recommendation-card__reason">
            ${escapeHtml(getRecommendationReason(product))}
          </p>
          <div class="cm-recommendation-card__footer">
            <span class="cm-recommendation-card__price">${escapeHtml(product.price)}</span>
            <button type="button" class="cm-recommendation-card__button"
              data-cm-add-recommendation data-variant-id="${escapeHtml(product.variantId)}">
              Add to Cart
            </button>
          </div>
        </div>
      </article>
    `;
  }

  function hasAllergies(preferences) {
    return Boolean(preferences) && (
      (Array.isArray(preferences.allergies) && preferences.allergies.length > 0) ||
      String(preferences.otherAllergies || '').trim().length >= 3
    );
  }

  function updateRecommendations(root, totals, weeklyGoals) {
    const sections = root.querySelectorAll('[data-cm-recommendations]');
    if (!sections.length) return;

    if (!weeklyGoals) {
      sections.forEach((section) => { section.hidden = true; });
      return;
    }

    const gaps = {
      calories: weeklyGoals.calories - totals.calories,
      protein: weeklyGoals.protein - totals.protein,
      carbs: weeklyGoals.carbs - totals.carbs,
      fat: weeklyGoals.fat - totals.fat
    };
    const primaryMacro = getPrimaryMacro(gaps, weeklyGoals);

    if (MACRO_KEYS.every((key) => gaps[key] <= 0)) {
      sections.forEach((section) => { section.hidden = true; });
      recommendationSignature = '';
      return;
    }

    const products = readRecommendationProducts(root);
    const cartProductIds = getCartProductIds(root);

    const recommendations = products
      .filter((product) => !cartProductIds.has(String(product.productId)))
      .map((product) => {
        const preference = getPreferenceMatch(product, mealPreferences);
        return {
          ...product,
          preferenceAllowed: preference.allowed,
          preferenceMatch: preference.preferred
        };
      })
      .filter((product) => product.preferenceAllowed)
      .map((product) => ({
        ...product,
        primaryMacro,
        primaryMacroValue: number(product[primaryMacro.key]),
        score: scoreProduct(product, gaps, weeklyGoals)
      }))
      .sort((left, right) =>
        right.primaryMacroValue - left.primaryMacroValue ||
        Number(right.preferenceMatch) - Number(left.preferenceMatch) ||
        right.score - left.score
      )
      .slice(0, MAX_RECOMMENDATIONS);

    const nextSignature = JSON.stringify({
      gaps: {
        calories: Math.round(gaps.calories),
        protein: Math.round(gaps.protein),
        carbs: Math.round(gaps.carbs),
        fat: Math.round(gaps.fat)
      },
      preferences: mealPreferences,
      products: recommendations.map((product) => product.variantId)
    });

    if (nextSignature === recommendationSignature) return;
    recommendationSignature = nextSignature;

    sections.forEach((section) => {
      const grid = section.querySelector('[data-cm-recommendations-grid]');
      const intro = section.querySelector('[data-cm-recommendations-intro]');
      if (!grid) return;

      section.hidden = false;

      if (!recommendations.length) {
        grid.innerHTML = hasAllergies(mealPreferences)
          ? '<p class="cm-recommendations__empty">We only suggest meals whose allergen information has been checked against your saved allergies, and none are available right now.</p>'
          : '<p class="cm-recommendations__empty">No additional recommendations are available right now.</p>';
        return;
      }

      if (intro) {
        intro.textContent =
          gaps.protein > 0
            ? `Your largest remaining gap is ${Math.round(primaryMacro.remaining).toLocaleString()} ${MACRO_UNITS[primaryMacro.key]} of ${MACRO_LABELS[primaryMacro.key]}. These meals are highest in that macro, then matched to your saved preferences.`
            : 'Your protein goal is covered. These meals best fit your remaining calories.';
        if (hasAllergies(mealPreferences)) {
          intro.textContent += ' Meals are filtered using your saved allergies — always check the ingredients before ordering.';
        }
      }

      grid.innerHTML = recommendations
        .map((product) => createRecommendationCard(product))
        .join('');
    });
  }

  function updateNutrition(root = document) {
    const totals = getCartTotals(root);
    const weeklyGoals = getWeeklyGoals();

    updateCartSummaries(root, totals);
    updateGoalsDashboard(root, totals, weeklyGoals);
    updateRecommendations(root, totals, weeklyGoals);
  }

  function scheduleNutritionUpdate(delay = 80) {
    clearTimeout(nutritionUpdateTimer);
    nutritionUpdateTimer = setTimeout(() => updateNutrition(document), delay);
  }

  document.addEventListener('input', (event) => {
    if (event.target.matches('input[name="updates[]"], input[type="number"], [data-quantity-input]')) {
      scheduleNutritionUpdate(30);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('input[name="updates[]"], input[type="number"], [data-quantity-input]')) {
      scheduleNutritionUpdate(30);
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[name="plus"], [name="minus"], quantity-selector button, .quantity-selector button')) {
      scheduleNutritionUpdate(50);
      setTimeout(() => scheduleNutritionUpdate(0), 350);
    }
  });

  const observer = new MutationObserver(() => {
    clearTimeout(observerUpdateTimer);
    observerUpdateTimer = setTimeout(() => updateNutrition(document), 120);
  });

  async function syncGoalsFromAccount() {
    try {
      const response = await fetch('/apps/nutrition-profile', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;

      const payload = await response.json();
      mealPreferences = payload && payload.preferences ? payload.preferences : null;
      if (payload && payload.goals) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload.goals));
      }
      recommendationSignature = '';
      updateNutrition(document);
      // Lets the per-week cart charts redraw once account goals arrive.
      document.dispatchEvent(new CustomEvent('cm:goals-updated'));
    } catch (error) {
      console.warn('Counting Macros account goals could not be loaded.', error);
    }
  }

  async function start() {
    // Saved goals and preferences are only needed where the goals dashboard or
    // recommendations are shown (the cart page), not on every page of the site.
    if (document.querySelector('[data-cm-goals-dashboard], [data-cm-week-goals-dashboard], [data-cm-recommendations]')) {
      await syncGoalsFromAccount();
    }
    updateNutrition(document);

    // Watch only the cart page and the cart drawer, not the whole page.
    document.querySelectorAll('#MinimogCart, m-cart-drawer').forEach((cartRoot) => {
      observer.observe(cartRoot, {
        childList: true,
        subtree: true
      });
    });
    document.addEventListener('cm:order-rules', () => scheduleNutritionUpdate(0));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
